import XPMotorsApi from "@xpeng/xpmotors-jsbridge-api";
import { isXPengApp } from "../../../../utils";

export type AppSdkMethod =
  | "Service.getCarInfo"
  | "Location.requestLocation"
  | string;

export interface AppSdkCallResult<T = any> {
  ok: boolean;
  method: AppSdkMethod;
  data?: T;
  raw?: any;
  errorCode?: string | number;
  errorMsg?: string;
  duration: number;
  timestamp: number;
}

export interface NormalizedCarInfo {
  plateNo?: string;
  vin?: string;
  vehicleId?: string;
  carId?: string;
  source?: string;
  raw?: any;
}

const SUCCESS_CODES = [0, 200, "0", "200"];
const SDK_LOG_KEY = "rescue_sdk_debug_logs";

function getBridge() {
  console.log("Attempting to get JSBridge instance...");
  return XPMotorsApi.getSdk();
}

function isSuccessResponse(response: any) {
  if (!response || typeof response !== "object") return true;
  if (typeof response.code === "undefined") return true;
  return SUCCESS_CODES.includes(response.code);
}

function getResponseData(response: any) {
  if (!response || typeof response !== "object") return response;
  return typeof response.data !== "undefined" ? response.data : response;
}

function getErrorMessage(error: any) {
  if (!error) return "unknown error";
  if (typeof error === "string") return error;
  return error.message || error.msg || JSON.stringify(error);
}

export async function callAppSdk<T = any>(
  method: AppSdkMethod,
  params: any = {},
  bridge = getBridge(),
): Promise<AppSdkCallResult<T>> {
  const start = Date.now();
  const timestamp = start;
  try {
    if (!isXPengApp()) {
      throw new Error("Not in XPeng App environment");
    }
    if (!bridge || typeof bridge.callHandler !== "function") {
      throw new Error("JSBridge callHandler is unavailable");
    }
    const raw = await bridge.callHandler(method, params);
    const ok = isSuccessResponse(raw);
    const result: AppSdkCallResult<T> = {
      ok,
      method,
      data: getResponseData(raw),
      raw,
      errorCode: ok ? undefined : raw?.code,
      errorMsg: ok
        ? undefined
        : raw?.msg || raw?.message || "SDK response code is not success",
      duration: Date.now() - start,
      timestamp,
    };
    recordSdkLog(result);
    return result;
  } catch (error) {
    const result: AppSdkCallResult<T> = {
      ok: false,
      method,
      errorMsg: getErrorMessage(error),
      raw: error,
      duration: Date.now() - start,
      timestamp,
    };
    recordSdkLog(result);
    return result;
  }
}

export function normalizeCarInfo(rawCarInfo: any): NormalizedCarInfo {
  const data = getResponseData(rawCarInfo) || {};
  return {
    plateNo:
      data.plateNo || data.plateNumber || data.carPlateNo || data.licensePlate,
    vin: data.vin || data.vinCode || data.vehicleVin,
    vehicleId: data.vehicleId || data.carVehicleId,
    carId: data.carId || data.id,
    source: data.source,
    raw: data,
  };
}

export async function getServiceCarInfo() {
  try {
    const result = await callAppSdk("Service.getCarInfo", {});
    return {
      ...result,
      data: result.ok ? normalizeCarInfo(result.data) : undefined,
    } as AppSdkCallResult<NormalizedCarInfo>;
  } catch (error) {
    throw error;
  }
}

export async function requestVehicleLocation(params: any = {}) {
  return callAppSdk("Location.requestLocation", params);
}

export function formatDistance(
  distanceMeters: number | string | null | undefined,
) {
  const distance = Number(distanceMeters);
  if (!Number.isFinite(distance) || distance < 0) return "";
  if (distance < 1000) return `${Math.round(distance)}m`;
  const km = Math.round((distance / 1000) * 100) / 100;
  return `${String(km).replace(/\.?0+$/, "")}km`;
}

export function getBridgeDebugInfo() {
  const bridge = getBridge();
  return {
    hasBridge: !!bridge,
    hasCallHandler: typeof bridge?.callHandler === "function",
    userAgent: window.navigator.userAgent,
    href: window.location.href,
  };
}

export function recordSdkLog(result: AppSdkCallResult) {
  const logItem = {
    ...result,
    page: window.location.pathname,
    userAgent: window.navigator.userAgent,
  };
  const logs = getStoredSdkLogs();
  const nextLogs = [logItem, ...logs].slice(0, 20);

  (window as any).__RESCUE_SDK_LOGS__ = nextLogs;
  console.info("[rescue-sdk]", logItem);

  try {
    window.localStorage.setItem(SDK_LOG_KEY, JSON.stringify(nextLogs));
  } catch (error) {
    console.warn("[rescue-sdk] localStorage write failed", error);
  }
}

export function getStoredSdkLogs() {
  try {
    const raw = window.localStorage.getItem(SDK_LOG_KEY);
    if (!raw) return [];
    const logs = JSON.parse(raw);
    return Array.isArray(logs) ? logs : [];
  } catch (error) {
    return [];
  }
}

export function clearStoredSdkLogs() {
  (window as any).__RESCUE_SDK_LOGS__ = [];
  window.localStorage.removeItem(SDK_LOG_KEY);
}
